import { AxiosResponse } from "axios";

import API from "../api";
import { AccessSettings } from "../models/access.settings";

import { Fleet } from "../models/fleet";
import { Instruction } from "../models/instruction";
import { OnboardingStage } from "../models/onboarding.stage";
import { Service } from "../models/service";
import { ServiceBooking as Material } from "../models/service.booking";
import { Staff } from "../models/staff";
import { Item } from "../models/item";
import { ILocationSymbol, IExceptionSymbol, IDescriptiveSymbol } from "../models/inventory";
import { Room } from "../models/room";
import { ItemTypes } from "../models/item.types";
import { JobMode } from "../models/job.mode";
import BaseService from "./_base.service";
import { UploadValueType } from "../util/enums";
import { Shipment } from "../models/shipments";
import { MemberStaff, MemberStaffWithoutTimezoneId } from "Models/members/member.staff";

const ROUTE_NAME_ACCESS = "accesses/settings/onboarding/update";
const ROUTE_NAME_FLEET = "fleets/onboarding/update";
const ROUTE_NAME_INSTRUCTIONS = "instruction/sales/onboarding/update";
const ROUTE_NAME_MATERIAL = "materials/onboarding/update";
const ROUTE_NAME_SERVICE = "services/onboarding/update";
const ROUTE_NAME_STAFF = "staffs/onboarding/update";
const ROUTE_ONBOARDING_MEMBERS = "members/onboarding/create";

const ROUTE_ONBOARDING = "onboarding/stage";

const ROUTE_NAME_ITEMS = "items/onboarding/update";
const ROUTE_NAME_PACKAGE_TYPE = "item/types/onboarding/update";
const ROUTE_NAME_SYMBOLS = "symbols/onboarding/update";
const ROUTE_NAME_LOCATION = "rooms/onboarding/update";
const ROUTE_NAME_MODE = "modes/onboarding/update";
const ROUTE_NAME_SHIPMENT = "tracking/shipments/onboarding/update";

const OnBoardingService = {
  ...BaseService<OnboardingStage>({ endpoint: ROUTE_ONBOARDING }),
  async updateAccess(
    accesses: AccessSettings[],
    companyId: number
  ): Promise<AxiosResponse<AccessSettings[]>> {
    return API.put(`/${ROUTE_NAME_ACCESS}/list/${companyId}`, accesses);
  },

  async updateDefault(
    companyId?: number,
    uploadType?: UploadValueType,
    branchId?: number
  ): Promise<AxiosResponse<boolean>> {
    return await API.put(`/${ROUTE_ONBOARDING}/default/${companyId}`, { uploadType, branchId });
  },

  async updateFleetList(fleets: Fleet[], companyId: number): Promise<AxiosResponse<Fleet[]>> {
    return await API.put(`/${ROUTE_NAME_FLEET}/list/${companyId}`, fleets);
  },

  async updateInstructions(
    instructions: Instruction[],
    companyId: number
  ): Promise<AxiosResponse<Instruction[]>> {
    return await API.put(`/${ROUTE_NAME_INSTRUCTIONS}/list/${companyId}`, instructions);
  },

  async updateMaterials(
    materials: Material[],
    companyId: number
  ): Promise<AxiosResponse<Material[]>> {
    return await API.put(`/${ROUTE_NAME_MATERIAL}/list/${companyId}`, materials);
  },

  async updateService(services: Service[], companyId: number): Promise<AxiosResponse<Service[]>> {
    return await API.put(`/${ROUTE_NAME_SERVICE}/list/${companyId}`, services);
  },

  async updateStaff(staffs: Staff[], companyId: number): Promise<AxiosResponse<Staff[]>> {
    return await API.put(`/${ROUTE_NAME_STAFF}/list/${companyId}`, staffs);
  },

  async createMembers(members: MemberStaffWithoutTimezoneId[], companyId: number): Promise<AxiosResponse<Staff[]>> {
    return await API.post(`/${ROUTE_ONBOARDING_MEMBERS}/list/${companyId}`, members);
  },

  async updateItems(items: Item[], companyId: number): Promise<AxiosResponse<Item[]>> {
    return await API.put(`/${ROUTE_NAME_ITEMS}/list/${companyId}`, items);
  },

  async updateExceptions(
    exceptions: IExceptionSymbol[],
    companyId: number
  ): Promise<AxiosResponse<IExceptionSymbol[]>> {
    return await API.put(`/${ROUTE_NAME_SYMBOLS}/list/${companyId}`, exceptions);
  },

  async updateExceptionLocation(
    exceptionLocations: ILocationSymbol[],
    companyId: number
  ): Promise<AxiosResponse<ILocationSymbol[]>> {
    return await API.put(`/${ROUTE_NAME_SYMBOLS}/list/${companyId}`, exceptionLocations);
  },

  async updatePreparationMethod(
    preparationMethods: IDescriptiveSymbol[],
    companyId: number
  ): Promise<AxiosResponse<IDescriptiveSymbol[]>> {
    return await API.put(`/${ROUTE_NAME_SYMBOLS}/list/${companyId}`, preparationMethods);
  },

  async updateLocations(locations: Room[], companyId: number): Promise<AxiosResponse<Room[]>> {
    return await API.put(`/${ROUTE_NAME_LOCATION}/list/${companyId}`, locations);
  },

  async updatePackageType(
    itemTypes: ItemTypes[],
    companyId: number
  ): Promise<AxiosResponse<ItemTypes[]>> {
    return await API.put(`/${ROUTE_NAME_PACKAGE_TYPE}/list/${companyId}`, itemTypes);
  },

  async updateModes(modes: JobMode[], companyId: number): Promise<AxiosResponse<JobMode[]>> {
    return await API.put(`/${ROUTE_NAME_MODE}/list/${companyId}`, modes);
  },

  async updateShipments(shipments: Shipment[], companyId: number): Promise<AxiosResponse<Shipment[]>> {
    return await API.put(`/${ROUTE_NAME_SHIPMENT}/list/${companyId}`, shipments);
  }
};

export default OnBoardingService;
